import { EmailListItem, EmailRef, isInbound, MailDirection } from "../EmailSearchAPI";
import { EmailSortColumn } from "../searchCriteria";
import { absent, blank, clock, optionalText, STATUS_PILL, subjectMarker } from "./emailFields";

interface iEmailTableProps {
    /** Which shape the rows are. Comes from the request, not from the payload. */
    direction: MailDirection;
    /**
     * Every item's `ref.direction` MUST equal `direction`. This component
     * does not check or filter that — it trusts the caller. There is a real
     * window (Task 5's search hook) where `filters.direction` has already
     * flipped while `items` still holds the previous direction's rows; a
     * caller that wires `direction={filters.direction}` and `items={items}`
     * straight through during that window renders a header for the new
     * direction against body rows narrowed (via `isInbound`) for the old
     * one, so header and body disagree on column count. Hold `items` back
     * (or clear it) until it matches the `direction` being rendered.
     */
    items: EmailListItem[];
    /** mailboxID to display name, from GetMailboxHealth. */
    mailboxNames: Map<number, string>;
    /**
     * Which ordering the rows are in. Required, not defaulted: these rows are
     * always sorted by something, and a default would let a caller render an
     * unmarked header over a sorted table.
     */
    sortBy: EmailSortColumn;
    sortAscending: boolean;
    onSort: (column: EmailSortColumn) => void;
    /**
     * Open one email. Required, not optional: a worklist whose rows look
     * activatable and do nothing is a failure nobody notices in review — the
     * same reasoning `TenantTable`'s `onInspect` carries.
     */
    onOpen: (ref: EmailRef) => void;
}

/**
 * The two sortable headers' tooltips.
 *
 * Both say what the column orders by rather than restating the label, because
 * in both cases the visible cell shows more than the sort key does.
 */
const SORT_TITLE: Record<EmailSortColumn, string> = {
    // §3.6. `stalled` is computed at read time over rows already selected and
    // is never a sort key, but it renders INSIDE this cell — so without this
    // the sort looks like it orders by the marker sitting beside the pill.
    status: "Sort by status. Stalled is computed when the rows are read and is never a sort key.",
    // Named for the same reason the filter is: both directions order on
    // UpdatedAt, and neither orders on receivedAt or sentAt, so a reader who
    // took this for "Received" would get a plausible order that is not that.
    lastActivity: "Sort by when processing last changed — not by when the email was received or sent.",
};

/**
 * A header that sorts, with the state annotated for assistive technology.
 *
 * `aria-sort` goes on the CELL and the glyph is hidden from it: the arrow is a
 * second rendering of what the attribute already says, and read out it would
 * announce the column twice. Non-sortable headers carry no `aria-sort` at all
 * — "none" means "sortable, not currently sorted", not "not sortable".
 *
 * A real `<button>`, so the header is reachable by keyboard and announced as
 * activatable. A click handler on the `<th>` would work with a mouse only.
 */
function SortHeader({
    column,
    label,
    sortBy,
    sortAscending,
    onSort,
}: {
    column: EmailSortColumn;
    label: string;
} & Pick<iEmailTableProps, "sortBy" | "sortAscending" | "onSort">) {
    const active = sortBy === column;

    return (
        <th
            scope="col"
            aria-sort={active ? (sortAscending ? "ascending" : "descending") : "none"}
        >
            <button
                type="button"
                className={active ? "mp-sort mp-sort-on" : "mp-sort"}
                title={SORT_TITLE[column]}
                onClick={() => onSort(column)}
            >
                {label}
                <span
                    className="mp-sort-mark"
                    aria-hidden="true"
                >
                    {active ? (sortAscending ? "▲" : "▼") : "▾"}
                </span>
            </button>
        </th>
    );
}

/**
 * The Attempts cell.
 *
 * Deliberately not `?? 0`: 0 attempts is a real report — the row was built
 * and has not yet been retried — while undefined means the row did not
 * report a count at all. Folding the two together turns "did not report"
 * into a false zero, which is the one defect this column exists to avoid.
 * Mirrors the mp-zero/mp-num split `Count` uses in MailboxRow for a
 * required number, extended with the absent branch that field never needs.
 */
function attempts(value?: number) {
    if (value === undefined) {
        return absent("This row did not report an attempt count");
    }
    return <span className={value === 0 ? "mp-zero" : "mp-num"}>{value.toLocaleString()}</span>;
}

function EmailTable({ direction, items, mailboxNames, sortBy, sortAscending, onSort, onOpen }: iEmailTableProps) {
    const inbound = direction === "Inbound";
    const sort = { sortBy, sortAscending, onSort };

    return (
        <div className="mp-table-scroll">
            <table className="mp-table mp-email-table">
                <thead>
                    <tr>
                        <SortHeader
                            column="status"
                            label="Status"
                            {...sort}
                        />
                        <th scope="col">Subject</th>
                        <th scope="col">From</th>
                        <th scope="col">To</th>
                        <th scope="col">Mailbox</th>
                        {inbound ? <th scope="col">Received</th> : <th scope="col">Origin</th>}
                        {!inbound && (
                            <th
                                scope="col"
                                className="mp-r"
                            >
                                Attempts
                            </th>
                        )}
                        {!inbound && <th scope="col">Sent</th>}
                        <SortHeader
                            column="lastActivity"
                            label="Last activity"
                            {...sort}
                        />
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            ID
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => {
                        const failed = item.lastFailureReason !== undefined;
                        // Undefined when there is a real subject to render.
                        const noSubject = subjectMarker(item.subject);
                        return (
                            <tr
                                key={`${item.ref.direction}-${item.ref.id}`}
                                className={failed ? "mp-email-row mp-email-row-fault" : "mp-email-row"}
                            >
                                <td>
                                    {item.status ? (
                                        <span className={STATUS_PILL[item.status]}>{item.status}</span>
                                    ) : (
                                        absent("This row reported no status")
                                    )}
                                    {/*
                                        Server-computed at read time (contract §2.3) and
                                        neither filterable nor sortable (spec §3.6), so it
                                        is shown and never offered as a control.
                                    */}
                                    {item.stalled && (
                                        <span
                                            role="img"
                                            aria-label="Stalled"
                                            title="Stalled — server-computed, not derived here"
                                            className="mp-dot mp-dot-stale mp-email-stalled"
                                        />
                                    )}
                                </td>
                                <td>
                                    {/*
                                        THE SUBJECT IS THE CONTROL, not the row and
                                        not the cell. A click handler on the `<tr>`
                                        cannot be reached by keyboard; the whole cell
                                        would swallow `lastFailureReason`, which is
                                        prose an operator will want to select and
                                        paste. `TenantTable` makes the tenant name
                                        the control for the same reason.

                                        Unlike `TenantTable`, there is no plain-text
                                        fallback: that table cannot open a row with
                                        no `tenantKey`, whereas every email row has a
                                        `ref`. An absent subject is exactly the row
                                        worth opening, so the marker itself is the
                                        label.
                                    */}
                                    <button
                                        type="button"
                                        className="mp-email-open"
                                        onClick={() => onOpen(item.ref)}
                                    >
                                        {noSubject ? (
                                            blank(noSubject.label, noSubject.title)
                                        ) : (
                                            <span className="mp-email-subject">{item.subject}</span>
                                        )}
                                    </button>
                                    {item.lastFailureReason !== undefined && (
                                        <span className="mp-email-reason">{item.lastFailureReason}</span>
                                    )}
                                </td>
                                <td>{optionalText(item.from, "Not recorded", "This message has no From header")}</td>
                                <td>{optionalText(item.to, "Not recorded", "This message has no To header")}</td>
                                <td>
                                    {item.mailboxID === undefined
                                        ? absent("Not recorded")
                                        : (mailboxNames.get(item.mailboxID) ?? `Mailbox ${item.mailboxID}`)}
                                </td>
                                {/*
                                    `isInbound` rather than an inline check on
                                    `item.ref.direction`: that is a NESTED discriminant and
                                    TypeScript 4.9 narrows only on direct properties, so the
                                    inline form compiles and narrows nothing.
                                */}
                                {isInbound(item) ? (
                                    <td>
                                        {clock(item.receivedAt)}
                                        {item.hasAttachments && (
                                            <span
                                                role="img"
                                                aria-label="Has attachments"
                                                title="Has attachments"
                                                className="mp-email-clip"
                                            >
                                                ▣
                                            </span>
                                        )}
                                    </td>
                                ) : (
                                    <>
                                        <td>{item.origin ?? absent("Not recorded")}</td>
                                        <td className="mp-r">{attempts(item.attemptCount)}</td>
                                        {/*
                                            An abandoned row can carry a sentAt from an earlier
                                            attempt, so this column states the timestamp and
                                            never implies the email was delivered.
                                        */}
                                        <td>{clock(item.sentAt)}</td>
                                    </>
                                )}
                                <td>{clock(item.processingUpdatedAt)}</td>
                                <td className="mp-r mp-email-id">{item.ref.id}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default EmailTable;
